import { useEffect, useRef, useState } from 'react';
import { useFormDraft } from './UnsavedChanges';
import { useQueryClient } from '@tanstack/react-query';
import { useArtifactCreate } from './artifact-command';
import { Field, DatePicker, Button, ErrorNotice, Notice } from '@yingbaba/ui';
import type { Artifact, StructuredBrief } from '@yingbaba/types';

const fields = [
  ['product', '品牌与产品'],
  ['objective', '营销目的'],
  ['audience', '目标人群'],
  ['key_message', '表达内容'],
  ['channels', '传播渠道'],
  ['deliverables', '交付内容与规格'],
  ['style', '参考风格'],
  ['constraints', '制作要求'],
  ['decision_maker', '确认负责人'],
] as const;
export function BriefInputs({
  initial,
  idPrefix = 'brief',
}: {
  initial?: StructuredBrief;
  idPrefix?: string;
}) {
  return (
    <>
      {fields.map(([name, label]) => {
        const id = `${idPrefix}-${name}`;
        return (
          <Field key={name} label={label} htmlFor={id}>
            <textarea
              id={id}
              name={`brief_${name}`}
              rows={name === 'constraints' ? 3 : 2}
              maxLength={2000}
              defaultValue={initial?.[name] || ''}
              placeholder="尚未确定可留空，生成后再确认"
            />
          </Field>
        );
      })}
      <div className="form-grid">
        <Field label="计划预算（元）" htmlFor={`${idPrefix}-budget`}>
          <input
            className="input"
            id={`${idPrefix}-budget`}
            type="number"
            name="brief_budget"
            min={0}
            max={1000000000}
            step={0.01}
            defaultValue={initial?.budget_minor ? initial.budget_minor / 100 : ''}
            placeholder="待确认"
          />
        </Field>
        <Field label="交付日期" htmlFor={`${idPrefix}-deadline`}>
          <DatePicker
            id={`${idPrefix}-deadline`}
            name="brief_deadline"
            type="date"
            defaultValue={initial?.deadline || ''}
          />
        </Field>
      </div>
      <Field label="待确认事项（每行一项）" htmlFor={`${idPrefix}-questions`}>
        <textarea
          id={`${idPrefix}-questions`}
          name="brief_questions"
          rows={3}
          maxLength={15000}
          defaultValue={initial?.open_questions?.join('\n') || ''}
        />
      </Field>
    </>
  );
}
export function briefFromForm(
  values: Record<string, FormDataEntryValue | string>,
): StructuredBrief {
  return {
    schema_version: 1,
    ...Object.fromEntries(
      fields.map(([name]) => [name, String(values[`brief_${name}`] || '').trim()]),
    ),
    budget_minor: Math.round(Number(values.brief_budget || 0) * 100),
    deadline: String(values.brief_deadline || ''),
    open_questions: String(values.brief_questions || '')
      .split('\n')
      .map((v) => v.trim())
      .filter(Boolean),
  } as StructuredBrief;
}
export function StructuredBriefEditor({
  artifact,
  write,
  onSaved,
  onDirty,
}: {
  artifact: Artifact;
  write: boolean;
  onSaved: (id: string) => void;
  onDirty?: (dirty: boolean) => void;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null);
  const qc = useQueryClient();
  const createArtifact = useArtifactCreate();
  const pending = useRef(false);
  const [epoch, setEpoch] = useState(0);
  const [completed, setCompleted] = useState('');
  const savedID = useRef('');
  const savedCallback = useRef(onSaved);
  savedCallback.current = onSaved;
  const draft = useFormDraft({
    enabled: write,
    busy,
    save: persist,
    discard: () => {
      setEpoch((n) => n + 1);
      onDirty?.(false);
    },
  });
  useEffect(() => {
    if (completed) savedCallback.current(completed);
  }, [completed]);
  let brief: StructuredBrief | undefined;
  try {
    brief = JSON.parse(artifact.structured_content) as StructuredBrief;
  } catch {
    /* Older briefs remain editable as text. */
  }
  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (await persist(event.currentTarget)) setCompleted(savedID.current);
  }
  async function persist(form: HTMLFormElement): Promise<boolean> {
    if (!write || pending.current || !form.reportValidity()) return false;
    pending.current = true;
    setBusy(true);
    setError(null);
    const value = briefFromForm(Object.fromEntries(new FormData(form)));
    try {
      const result = await createArtifact({
        kind: 'brief',
        title: artifact.title,
        parent_id: artifact.id,
        input: artifact.input.split('\n用户确认的结构化 Brief：\n')[0],
        content: '人工确认的创作需求',
        brief: value,
      });
      await qc.invalidateQueries();
      savedID.current = result.id;
      draft.clean();
      onDirty?.(false);
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <form
      key={epoch}
      ref={draft.ref}
      className="panel form-stack creative-brief-editor"
      onSubmit={save}
      onChange={() => {
        draft.onChange();
        onDirty?.(true);
      }}
    >
      <Notice>
        逐项核对需求后保存新版本。目标、受众和交付内容完整后即可锁定；待确认事项不会自动成为合同条件。
      </Notice>
      <ErrorNotice error={error} />
      <fieldset
        disabled={!write || busy}
        style={{ border: 0, padding: 0, margin: 0 }}
        className="form-stack"
      >
        <BriefInputs initial={brief} idPrefix={`brief-edit-${artifact.id}`} />
      </fieldset>
      <Button type="submit" loading={busy} disabled={!write}>
        保存创作需求新版本
      </Button>
    </form>
  );
}
